import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, Plane, ShoppingCart, DollarSign, TrendingUp, AlertTriangle,
  ShieldCheck, Wallet, Settings as SettingsIcon,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Spinner, StatusBadge } from "../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import api, { apiError } from "../lib/api";
import { rupiah } from "../lib/format";

const Stat = ({ icon: Icon, label, value, tone = "navy" }) => (
  <div className="bg-white rounded-2xl p-4 shadow-soft border border-black/5" data-testid={`stat-${label}`}>
    <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${tone === "lime" ? "bg-lime-light" : "bg-cloud"}`}><Icon size={17} className="text-navy" /></div>
    <div className="font-heading font-black text-xl text-navy">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

export default function Admin() {
  const [d, setD] = useState(null);
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = () => Promise.all([
    api.get("/admin/dashboard"), api.get("/admin/users"), api.get("/admin/withdrawals"),
    api.get("/admin/verifications"), api.get("/admin/disputes"), api.get("/admin/settings"),
  ]).then(([a, b, c, e, f, g]) => {
    setD(a.data); setUsers(b.data); setWithdrawals(c.data); setVerifs(e.data); setDisputes(f.data); setSettings(g.data);
  }).finally(() => setLoading(false));
  useEffect(() => { loadAll(); }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); loadAll(); } catch (e) { toast.error(apiError(e)); } };

  if (loading || !d) return <Spinner />;

  const chartData = d.top_destinations.map((t) => ({ name: t.name, value: t.count }));
  const COLORS = ["#102A43", "#4DA3FF", "#B7E33A", "#9ECB26", "#1B3A5B", "#4DA3FF"];

  return (
    <div className="space-y-6">
      <h1 className="font-heading font-extrabold text-2xl text-navy flex items-center gap-2"><ShieldCheck className="text-sky" /> Admin Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Total users" value={d.total_users} />
        <Stat icon={Plane} label="Active trips" value={d.active_trips} />
        <Stat icon={ShoppingCart} label="Total orders" value={d.total_orders} />
        <Stat icon={TrendingUp} label="Completed" value={d.completed_orders} tone="lime" />
        <Stat icon={DollarSign} label="GMV" value={rupiah(d.gmv)} />
        <Stat icon={DollarSign} label="Revenue" value={rupiah(d.revenue)} tone="lime" />
        <Stat icon={AlertTriangle} label="Disputes" value={d.pending_disputes} />
        <Stat icon={Wallet} label="Pending payouts" value={d.pending_withdrawals} />
      </div>

      <Tabs defaultValue="analytics">
        <TabsList className="rounded-full bg-white border border-black/5 p-1 flex-wrap h-auto">
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-full">Analytics</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full">Users</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="admin-tab-verif" className="rounded-full">Verifications</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals" className="rounded-full">Withdrawals</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes" className="rounded-full">Disputes</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-full">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4">
          <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
            <div className="font-heading font-bold text-navy mb-3">Top Destinations (orders)</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="value" radius={[8, 8, 0, 0]}>{chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
            <div className="font-heading font-bold text-navy mb-3">Top BAWAINERs</div>
            <div className="space-y-2">
              {d.top_bawainers.map((b) => (
                <div key={b.id} className="flex items-center gap-3">
                  <img src={b.avatar} alt="" className="w-8 h-8 rounded-full" />
                  <span className="font-medium text-navy text-sm flex-1">{b.name}</span>
                  <span className="text-xs text-muted-foreground">{b.completed_trips} trips · ⭐ {b.rating}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="bg-white rounded-3xl shadow-soft border border-black/5 divide-y divide-black/5">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-3" data-testid={`admin-user-${u.id}`}>
                <img src={u.avatar} alt="" className="w-9 h-9 rounded-full" />
                <div className="flex-1 min-w-0"><div className="font-semibold text-navy text-sm truncate">{u.name}</div><div className="text-xs text-muted-foreground truncate">{u.email} · {u.role}</div></div>
                {u.role !== "admin" && (
                  <button onClick={() => act(() => api.patch(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended }), u.suspended ? "Diaktifkan" : "Disuspend")}
                    data-testid={`suspend-${u.id}`} className={`text-xs font-bold rounded-full px-3 py-1.5 ${u.suspended ? "bg-lime-light text-navy" : "bg-red-50 text-red-600"}`}>
                    {u.suspended ? "Reactivate" : "Suspend"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="verifications">
          {verifs.length === 0 ? <p className="text-sm text-muted-foreground py-6">Tidak ada verifikasi pending.</p> : (
            <div className="space-y-2">
              {verifs.map((v) => (
                <div key={v.user_id} className="bg-white rounded-2xl p-4 shadow-soft border border-black/5 flex items-center gap-3" data-testid={`verif-${v.user_id}`}>
                  <img src={v.user?.avatar} alt="" className="w-9 h-9 rounded-full" />
                  <div className="flex-1"><div className="font-semibold text-navy text-sm">{v.user?.name}</div><div className="text-xs text-muted-foreground">{v.phone}</div></div>
                  <button onClick={() => act(() => api.patch(`/admin/verifications/${v.user_id}`, { decision: "verified" }), "Verified")} data-testid={`approve-verif-${v.user_id}`} className="text-xs font-bold bg-lime text-navy rounded-full px-3 py-1.5">Approve</button>
                  <button onClick={() => act(() => api.patch(`/admin/verifications/${v.user_id}`, { decision: "rejected" }), "Rejected")} data-testid={`reject-verif-${v.user_id}`} className="text-xs font-bold bg-red-50 text-red-600 rounded-full px-3 py-1.5">Reject</button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="withdrawals">
          {withdrawals.length === 0 ? <p className="text-sm text-muted-foreground py-6">Tidak ada penarikan.</p> : (
            <div className="space-y-2">
              {withdrawals.map((w) => (
                <div key={w.id} className="bg-white rounded-2xl p-4 shadow-soft border border-black/5 flex items-center gap-3">
                  <div className="flex-1"><div className="font-semibold text-navy text-sm">{rupiah(w.amount)} · {w.user?.name}</div><div className="text-xs text-muted-foreground">{w.method}</div></div>
                  <StatusBadge status={w.status} />
                  {w.status === "pending" && (
                    <>
                      <button onClick={() => act(() => api.patch(`/admin/withdrawals/${w.id}`, { status: "completed" }), "Completed")} data-testid={`wd-approve-${w.id}`} className="text-xs font-bold bg-lime text-navy rounded-full px-3 py-1.5">Pay</button>
                      <button onClick={() => act(() => api.patch(`/admin/withdrawals/${w.id}`, { status: "rejected" }), "Rejected")} data-testid={`wd-reject-${w.id}`} className="text-xs font-bold bg-red-50 text-red-600 rounded-full px-3 py-1.5">Reject</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="disputes">
          {disputes.length === 0 ? <p className="text-sm text-muted-foreground py-6">Tidak ada dispute.</p> : (
            <div className="space-y-2">
              {disputes.map((ds) => (
                <div key={ds.id} className="bg-white rounded-2xl p-4 shadow-soft border border-black/5" data-testid={`dispute-${ds.id}`}>
                  <div className="flex items-center justify-between"><span className="font-semibold text-navy text-sm">{ds.reason}</span><StatusBadge status={ds.status} /></div>
                  <p className="text-xs text-muted-foreground mt-1">{ds.description}</p>
                  {ds.status === "open" && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => act(() => api.patch(`/admin/disputes/${ds.id}`, { resolution: "refund_buyer" }), "Buyer refunded")} data-testid={`refund-${ds.id}`} className="text-xs font-bold bg-orange-100 text-orange-700 rounded-full px-3 py-1.5">Refund Buyer</button>
                      <button onClick={() => act(() => api.patch(`/admin/disputes/${ds.id}`, { resolution: "release_funds" }), "Funds released")} data-testid={`release-${ds.id}`} className="text-xs font-bold bg-lime text-navy rounded-full px-3 py-1.5">Release Funds</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings">
          <div className="bg-white rounded-3xl p-5 shadow-soft border border-black/5 space-y-3 max-w-md">
            <div className="font-heading font-bold text-navy flex items-center gap-2"><SettingsIcon size={17} /> Platform Settings</div>
            <div><label className="text-sm font-semibold text-ink">Commission rate (0.10 = 10%)</label>
              <Input data-testid="setting-commission" type="number" step="0.01" defaultValue={settings.commission_rate}
                onBlur={(e) => act(() => api.patch("/admin/settings", { commission_rate: Number(e.target.value) }), "Tersimpan")} className="rounded-xl mt-1" /></div>
            <div><label className="text-sm font-semibold text-ink">Order timeout (days)</label>
              <Input data-testid="setting-timeout" type="number" defaultValue={settings.order_timeout_days}
                onBlur={(e) => act(() => api.patch("/admin/settings", { order_timeout_days: Number(e.target.value) }), "Tersimpan")} className="rounded-xl mt-1" /></div>
            <div><label className="text-sm font-semibold text-ink">Quotation TTL (hours)</label>
              <Input data-testid="setting-ttl" type="number" defaultValue={settings.quotation_ttl_hours}
                onBlur={(e) => act(() => api.patch("/admin/settings", { quotation_ttl_hours: Number(e.target.value) }), "Tersimpan")} className="rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold text-ink">PRO price</label>
                <Input data-testid="setting-pro" type="number" defaultValue={settings.membership_pro}
                  onBlur={(e) => act(() => api.patch("/admin/settings", { membership_pro: Number(e.target.value) }), "Tersimpan")} className="rounded-xl mt-1" /></div>
              <div><label className="text-sm font-semibold text-ink">POWER price</label>
                <Input data-testid="setting-power" type="number" defaultValue={settings.membership_power}
                  onBlur={(e) => act(() => api.patch("/admin/settings", { membership_power: Number(e.target.value) }), "Tersimpan")} className="rounded-xl mt-1" /></div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
